import { Sheet } from 'react-modal-sheet';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button, ButtonToggle, RoundBadge } from '@peacecover/component';
import { ChevronDownSmall, ChevronUpSmall } from '@peacecover/icon';
import BettieAgreementSection from '../../../presentation/chat/components/BettieAgreementSection';
import Bubble from '../../../presentation/components/floating/Bubble';
import { useBettieCharge } from '../../../presentation/chat/hooks/useBettieCharge';
import type { components } from '../../../types/schema';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';

interface BettieChargeBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

type ApiV1BettieProduct = components['schemas']['ApiV1BettieProduct'];

const BettieChargeBottomSheet = ({ isOpen, onClose }: BettieChargeBottomSheetProps) => {
  const { colorMode } = useColorMode();
  const {
    bettieProducts,
    isBettieProductsLoading,
    isUserFetching,
    selectedItem,
    isItemListOpen,
    setIsItemListOpen,
    handleSelectItem,
    handlePrimaryClick
  } = useBettieCharge(onClose);

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container className="!rounded-t-[20px] !bg-background-light dark:!bg-background-elevated">
        <div className="px-[24px] py-[40px] flex flex-col gap-[32px]">
          <div className="flex flex-col gap-[10px] relative">
            <Bubble
              text={t('BettieChargeBottomSheet.bubble')}
              gradientDirection="center"
              className="!absolute left-[36px] top-[-15px] z-[1] pointer-events-none"
            />
            {isBettieProductsLoading || isUserFetching || !bettieProducts?.length ? (
              <BettieProductSelectSkeleton />
            ) : (
              (isItemListOpen
                ? bettieProducts
                : bettieProducts.filter(
                    (item) => item.bettieProductId === selectedItem?.bettieProductId
                  )
              ).map((item) =>
                selectedItem?.bettieProductId === item.bettieProductId ? (
                  <div
                    key={item.bettieProductId}
                    className="p-[1px] rounded-[17px] shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_8px_0_rgba(0,0,0,0.07)]"
                    style={{
                      background:
                        BettieGradientColor.bettieProductOutline[colorMode as 'light' | 'dark']
                          .background
                    }}
                  >
                    <button
                      type="button"
                      className="flex items-center justify-between px-[19px] py-[19px] dark:px-[20px] dark:py-[20px] rounded-[16px] w-full"
                      style={{
                        background:
                          BettieGradientColor.bettieProductFill[colorMode as 'light' | 'dark']
                            .background
                      }}
                      onClick={() => handleSelectItem(item.bettieProductId)}
                    >
                      <ChargeItem
                        product={item}
                        isSelected={selectedItem?.bettieProductId === item.bettieProductId}
                      />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    key={item.bettieProductId}
                    className="flex items-center justify-between rounded-[16px] dark:border dark:border-[#141414] shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_8px_0_rgba(0,0,0,0.07)] dark:shadow-[0_0_3px_0_rgba(0,0,0,0.95),_0_0_12px_0_rgba(0,0,0,0.00)] px-[20px] py-[20px]"
                    onClick={() => handleSelectItem(item.bettieProductId)}
                  >
                    <ChargeItem
                      product={item}
                      isSelected={selectedItem?.bettieProductId === item.bettieProductId}
                    />
                  </button>
                )
              )
            )}
            {bettieProducts?.length && bettieProducts?.length > 1 && (
              <button
                type="button"
                onClick={() => setIsItemListOpen((prev) => !prev)}
                className="text-[13px] leading-[16px] text-text-main-lv3 flex items-center justify-center gap-[4px]"
              >
                {isItemListOpen
                  ? t('BettieChargeBottomSheet.collapse')
                  : t('BettieChargeBottomSheet.loadMore')}
                {isItemListOpen ? (
                  <ChevronUpSmall size={18} className="text-text-main-lv3" />
                ) : (
                  <ChevronDownSmall size={18} className="text-text-main-lv3" />
                )}
              </button>
            )}
          </div>
          <BettieAgreementSection />
        </div>
        <Button
          size="large"
          className="mx-[24px] rounded-[18px] text-[17px] font-semibold leading-[20px]"
          onClick={handlePrimaryClick}
        >
          {t('BettieChargeBottomSheet.goToPayment')}
        </Button>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
    </Sheet>
  );
};

export default BettieChargeBottomSheet;

const ChargeItem = ({
  product,
  isSelected
}: {
  product: ApiV1BettieProduct;
  isSelected: boolean;
}) => {
  const isSale = product.discountAmount > 0;
  const amount = Number(product.baseAmount - product.discountAmount).toLocaleString();

  return (
    <>
      <div className="flex items-center">
        <ButtonToggle type="check" size="lg" checked={isSelected} />
        <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv2 text-left max-w-[187px] ml-[6px] mr-[4px]">
          {product.name}
        </p>
        {isSale && <RoundBadge colorVariant="secondary">SALE</RoundBadge>}
      </div>
      {isSale ? (
        <div className="flex flex-col items-end gap-[2px]">
          <p className="text-[15px] font-bold leading-[18px] bg-gradient-to-r from-[#FF45A9] to-[#FF6A6A] bg-clip-text text-transparent">
            {t('BettieChargeBottomSheet.amount', { amount })}
          </p>
          <p className="text-[12px] font-medium leading-[14px] text-text-sub-lv1 line-through">
            {t('BettieChargeBottomSheet.amount', {
              amount: Number(product.baseAmount).toLocaleString()
            })}
          </p>
        </div>
      ) : (
        <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv2">
          {t('BettieChargeBottomSheet.amount', { amount })}
        </p>
      )}
    </>
  );
};

const BettieProductSelectSkeleton = () => {
  return (
    <div className="flex items-center justify-between rounded-[16px] dark:border dark:border-[#141414] shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_8px_0_rgba(0,0,0,0.07)] dark:shadow-[0_0_3px_0_rgba(0,0,0,0.95),_0_0_12px_0_rgba(0,0,0,0.00)] px-[20px] py-[20px] animate-pulse">
      <div className="flex items-center gap-[6px]">
        <div className="h-[24px] w-[24px] rounded-full bg-gray-100 dark:bg-gray-800" />
        <div className="h-[18px] w-[160px] rounded-full bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="h-[18px] w-[60px] rounded-full bg-gray-100 dark:bg-gray-800" />
    </div>
  );
};
